import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClients, useContracts, useConvenios } from '../../contexts/DataContext';
import { useUser } from '../../components/UserContext';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import { Input } from '../../components/input';
import { Button } from '../../components/button';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '../../components/table';
import { ContratoView } from '../../components/ContratoView';
import { usePortalView } from '../../contexts/PortalViewContext';

const ClientPortalContratos = () => {
  const { clientsList, clientsById, clientsLoading } = useClients();
  const { contractsList, contractsLoading } = useContracts();
  const { conveniosById } = useConvenios();
  const { userData } = useUser();
  const { activeClienteId } = usePortalView();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filterClientId, setFilterClientId] = useState('all');
  const [selectedContrato, setSelectedContrato] = useState(null);
  const [isContratoViewOpen, setIsContratoViewOpen] = useState(false);

  const loading = clientsLoading || contractsLoading;

  // The Empresa page owns the fiado's contratos now — an active fiado context
  // lands on its Contratos tab. The no-context fallback keeps the old
  // aggregated table (convenio contracts included).
  useEffect(() => {
    if (activeClienteId) {
      navigate(`/app/portal/cliente/${activeClienteId}?tab=contratos`, { replace: true });
    }
  }, [activeClienteId, navigate]);

  const resetGuardRef = useRef(true);
  useEffect(() => {
    if (resetGuardRef.current) { resetGuardRef.current = false; return; }
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage, filterClientId]);

  // The portal context scopes this page to ONE empresa: no other fiados and
  // no convenio contracts mixed in. Without an active cliente context (legacy
  // direct URL) it falls back to the old aggregated behavior.
  const scopedClients = useMemo(
    () => (activeClienteId ? clientsList.filter((c) => c.id === activeClienteId) : clientsList),
    [clientsList, activeClienteId]
  );

  const clientNames = useMemo(() => {
    const names = {};
    scopedClients.forEach((c) => { names[c.id] = c.nombreEmpresa || 'Sin nombre'; });
    return names;
  }, [scopedClients]);

  // Build parent name for display (client or convenio)
  const getContractParentName = (c) => {
    if (c.convenioId) {
      const conv = conveniosById[c.convenioId];
      const ben = (conv?.beneficiarios || []).find((b) => b.id === c.beneficiarioId);
      return `${conv?.nombre || 'Convenio'} ${ben ? `- ${ben.nombre}` : ''}`;
    }
    return clientNames[c.clientId] || 'Sin nombre';
  };

  // Filter contracts: client contracts + convenio contracts the user can access
  const filteredContratos = useMemo(() => {
    const clientIds = new Set(scopedClients.map((c) => c.id));
    const convenioAccess = activeClienteId ? {} : userData?.convenioAccess || {};

    let contracts = contractsList.filter((c) => {
      // Client contract
      if (c.clientId && clientIds.has(c.clientId)) return true;
      // Convenio contract
      if (c.convenioId && c.convenioId in convenioAccess) {
        const access = convenioAccess[c.convenioId];
        if (access.fullAccess) return true;
        if (c.proyectoId && (access.proyectoIds || []).includes(c.proyectoId)) return true;
      }
      return false;
    });

    if (filterClientId !== 'all') {
      contracts = contracts.filter((c) => c.clientId === filterClientId);
    }

    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      contracts = contracts.filter((c) => {
        const parentName = getContractParentName(c).toLowerCase();
        const title = c.tituloContrato?.value?.toLowerCase() || '';
        const contratante = c.contratante?.value?.toLowerCase() || '';
        return parentName.includes(s) || title.includes(s) || contratante.includes(s);
      });
    }

    return contracts;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractsList, scopedClients, activeClienteId, clientNames, filterClientId, searchTerm, userData?.convenioAccess, conveniosById]);

  // Clamp page
  useEffect(() => {
    if (loading) return;
    if (filteredContratos.length === 0) return;
    const maxPage = Math.max(1, Math.ceil(filteredContratos.length / itemsPerPage));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [loading, filteredContratos.length, itemsPerPage, currentPage]);

  const handleContratoClick = (contrato) => {
    setSelectedContrato(contrato);
    setIsContratoViewOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-azul-surelia"></div>
      </div>
    );
  }

  const totalPages = Math.ceil(filteredContratos.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedContratos = filteredContratos.slice(startIndex, endIndex);

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <Heading level={1} className="text-azul-surelia mb-2">Contratos</Heading>
        <Text className="text-zinc-600">
          {filteredContratos.length} contrato{filteredContratos.length !== 1 ? 's' : ''}
        </Text>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white rounded-lg shadow-sm border border-zinc-200 p-3 sm:p-4">
        <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 sm:gap-3 sm:items-center sm:flex-wrap">
          {scopedClients.length > 1 && (
            <select
              value={filterClientId}
              onChange={(e) => setFilterClientId(e.target.value)}
              className="col-span-2 px-3 py-2 border border-azul-surelia/30 rounded-lg text-azul-surelia bg-white focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none text-sm"
            >
              <option value="all">Todas las empresas</option>
              {scopedClients.map((c) => (
                <option key={c.id} value={c.id}>{c.nombreEmpresa || 'Sin nombre'}</option>
              ))}
            </select>
          )}

          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="px-3 py-2 border border-azul-surelia/30 rounded-lg text-azul-surelia bg-white focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none text-sm"
          >
            <option value={5}>5 por pág.</option>
            <option value={10}>10 por pág.</option>
            <option value={20}>20 por pág.</option>
            <option value={50}>50 por pág.</option>
          </select>

          <Input
            type="text"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="col-span-2 sm:col-span-1 sm:flex-1 sm:max-w-xs"
          />
        </div>
      </div>

      {/* Contratos table */}
      <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-x-auto">
        <div className="overflow-x-auto">
          <Table>
            <TableHead className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95">
              <TableRow>
                {clientsList.length > 1 && (
                  <TableHeader className="text-white font-semibold py-4">Empresa</TableHeader>
                )}
                <TableHeader className="text-white font-semibold py-4">Requeridas</TableHeader>
                <TableHeader className="text-white font-semibold py-4">Emitidas</TableHeader>
                <TableHeader className="text-white font-semibold py-4">Estatus Contrato</TableHeader>
                <TableHeader className="text-white font-semibold py-4">Monto</TableHeader>
                <TableHeader className="text-white font-semibold py-4">Título del Contrato</TableHeader>
                <TableHeader className="text-white font-semibold py-4">Beneficiario</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedContratos.map((contrato, index) => (
                <TableRow
                  key={contrato.id}
                  className={`cursor-pointer transition-all duration-200 hover:bg-azul-surelia/5 ${
                    index % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'
                  }`}
                  onClick={() => handleContratoClick(contrato)}
                >
                  {clientsList.length > 1 && (
                    <TableCell className="py-4">
                      <Text className="text-sm font-medium text-azul-surelia">
                        {clientNames[contrato.clientId] || 'Cargando...'}
                      </Text>
                    </TableCell>
                  )}

                  <TableCell className="py-4">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-700 font-semibold text-sm">
                      {contrato.fianzasRequeridas?.value ? contrato.fianzasRequeridas.value.length : 0}
                    </span>
                  </TableCell>
                  <TableCell className="py-4">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-700 font-semibold text-sm">
                      {contrato.fianzas ? contrato.fianzas.length : 0}
                    </span>
                  </TableCell>

                  <TableCell className="py-4">
                    <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${
                      contrato.estatus?.value === 'activo' ? 'bg-green-100 text-green-800' :
                      contrato.estatus?.value === 'completado' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {contrato.estatus?.value || 'N/A'}
                    </span>
                  </TableCell>

                  <TableCell className="py-4">
                    <Text className="text-sm font-semibold text-azul-surelia">
                      {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(contrato.montoContrato?.value || 0)}
                    </Text>
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="max-w-xs">
                      <Text
                        className="text-sm font-semibold text-zinc-900 truncate"
                        title={contrato.tituloContrato?.value || 'Sin título'}
                      >
                        {contrato.tituloContrato?.value || 'Sin título'}
                      </Text>
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <Text className="text-sm text-zinc-900">
                      {contrato.beneficiario?.value || contrato.contratante?.value || 'N/A'}
                    </Text>
                  </TableCell>
                </TableRow>
              ))}
              {paginatedContratos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={clientsList.length > 1 ? 7 : 6} className="py-8 text-center text-zinc-500">
                    No se encontraron contratos
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {filteredContratos.length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-zinc-200 p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-zinc-600">
              Mostrando {startIndex + 1} - {Math.min(endIndex, filteredContratos.length)} de {filteredContratos.length} contratos
            </div>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <Button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                outline
                color="surelia"
                className="text-sm px-3 py-2"
              >
                Anterior
              </Button>
              <div className="flex items-center gap-1">
                {(() => {
                  const pageNumbers = [];
                  const maxVisiblePages = 5;
                  const tp = Math.max(1, totalPages);
                  if (tp <= maxVisiblePages) {
                    for (let i = 1; i <= tp; i++) pageNumbers.push(i);
                  } else {
                    pageNumbers.push(1);
                    if (currentPage <= 2) {
                      for (let i = 2; i <= Math.min(3, tp - 1); i++) pageNumbers.push(i);
                      if (tp > 3) pageNumbers.push('...');
                    } else if (currentPage >= tp - 1) {
                      if (tp > 3) pageNumbers.push('...');
                      for (let i = Math.max(tp - 2, 2); i <= tp - 1; i++) pageNumbers.push(i);
                    } else {
                      pageNumbers.push('...');
                      pageNumbers.push(currentPage);
                      pageNumbers.push('...');
                    }
                    if (tp > 1) pageNumbers.push(tp);
                  }
                  return pageNumbers.map((page, i) =>
                    page === '...' ? (
                      <span key={`ellipsis-${i}`} className="px-2 text-zinc-400">...</span>
                    ) : (
                      <Button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        color={currentPage === page ? 'surelia' : 'white'}
                        className="text-sm px-3 py-2 min-w-[40px]"
                      >
                        {page}
                      </Button>
                    )
                  );
                })()}
              </div>
              <Button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
                outline
                color="surelia"
                className="text-sm px-3 py-2"
              >
                Siguiente
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ContratoView Modal */}
      {selectedContrato && (
        <ContratoView
          isOpen={isContratoViewOpen}
          onClose={() => { setIsContratoViewOpen(false); setSelectedContrato(null); }}
          contrato={selectedContrato}
          clientData={clientsById[selectedContrato.clientId]}
          onUpdate={() => {}}
        />
      )}
    </div>
  );
};

export default ClientPortalContratos;
